import React, { useEffect, useState } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import api from '@/lib/api';

interface HeartbeatPulse {
  userId: string;
  username: string;
  exerciseName: string;
  setNumber: number;
  reps: number;
  weight?: number;
  timestamp: number;
}

interface ActiveFriend {
  userId: string;
  username: string;
  exerciseName: string;
  setNumber: number;
  reps: number;
  weight?: number;
  lastPulse: number;
}

/**
 * Heartbeat Component
 * Shows real-time activity pulses from friends
 * Displays who's currently working out and what they're doing
 */
export const Heartbeat: React.FC = () => {
  const { isConnected } = useSocket();
  const [activeFriends, setActiveFriends] = useState<Map<string, ActiveFriend>>(new Map());
  const [showHeartbeats, setShowHeartbeats] = useState(false);

  // Fetch initial heartbeats
  useEffect(() => {
    const fetchHeartbeats = async () => {
      try {
        const response = await api.get('/heartbeat/friends');
        if (response.data.heartbeats) {
          const friendsMap = new Map<string, ActiveFriend>();
          response.data.heartbeats.forEach((hb: HeartbeatPulse) => {
            friendsMap.set(hb.userId, {
              userId: hb.userId,
              username: hb.username,
              exerciseName: hb.exerciseName,
              setNumber: hb.setNumber,
              reps: hb.reps,
              weight: hb.weight,
              lastPulse: hb.timestamp,
            });
          });
          setActiveFriends(friendsMap);
        }
      } catch (error) {
        console.error('Failed to fetch heartbeats:', error);
      }
    };

    if (isConnected) {
      fetchHeartbeats();
      // Refresh every 10 seconds
      const interval = setInterval(fetchHeartbeats, 10000);
      return () => clearInterval(interval);
    }
  }, [isConnected]);

  // Listen for heartbeat pulses
  useEffect(() => {
    const handleHeartbeatPulse = (event: Event) => {
      const customEvent = event as CustomEvent<HeartbeatPulse>;
      const data = customEvent.detail;

      setActiveFriends(prev => {
        const updated = new Map(prev);
        updated.set(data.userId, {
          userId: data.userId,
          username: data.username,
          exerciseName: data.exerciseName,
          setNumber: data.setNumber,
          reps: data.reps,
          weight: data.weight,
          lastPulse: data.timestamp,
        });
        return updated;
      });

      // Trigger pulse animation
      triggerPulseAnimation(data.userId);
    };

    window.addEventListener('heartbeat-pulse', handleHeartbeatPulse);
    return () => window.removeEventListener('heartbeat-pulse', handleHeartbeatPulse);
  }, []);

  // Remove stale heartbeats (older than 30 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setActiveFriends(prev => {
        const updated = new Map(prev);
        for (const [userId, friend] of updated.entries()) {
          if (now - friend.lastPulse > 30000) {
            updated.delete(userId);
          }
        }
        return updated;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const triggerPulseAnimation = (userId: string) => {
    const element = document.getElementById(`heartbeat-${userId}`);
    if (element) {
      element.classList.add('pulse-animation');
      setTimeout(() => {
        element.classList.remove('pulse-animation');
      }, 600);
    }
  };

  // A red button with a heart emoji used to float over every screen whether or
  // not anybody was training, and on phones it landed on top of the bottom tab
  // bar. It now appears only when there is something to show, and clears the
  // tab bar on small screens.
  if (!isConnected || activeFriends.size === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-24 right-4 lg:bottom-6 lg:right-6 z-40">
      {/* Toggle Button */}
      <button
        onClick={() => setShowHeartbeats(!showHeartbeats)}
        aria-expanded={showHeartbeats}
        className="flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-surface border border-line
                   shadow-depth text-sm font-semibold text-ink hover:border-accent/40 transition-colors"
        title="Friends working out"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
        </span>
        {activeFriends.size} training now
      </button>

      {/* Heartbeat List */}
      {showHeartbeats && (
        <div className="absolute bottom-14 right-0 bg-surface rounded-2xl shadow-depth p-4 w-72 max-h-96 overflow-y-auto border border-line">
          <h3 className="section-title mb-3">Training right now</h3>

          <div className="space-y-2">
            {Array.from(activeFriends.values()).map(friend => (
              <div
                key={friend.userId}
                id={`heartbeat-${friend.userId}`}
                className="p-3 bg-subtle rounded-xl border border-line transition-all duration-300"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-ink text-sm">
                      {friend.username}
                    </p>
                    <p className="text-xs text-ink-muted mt-1">
                      {friend.exerciseName}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-ink-muted">
                      <span className="bg-red-100 dark:bg-red-900/40 px-2 py-1 rounded">
                        Set {friend.setNumber}
                      </span>
                      <span className="bg-blue-100 dark:bg-blue-900/40 px-2 py-1 rounded">
                        {friend.reps} reps
                      </span>
                      {friend.weight && (
                        <span className="bg-green-100 dark:bg-green-900/40 px-2 py-1 rounded">
                          {friend.weight}kg
                        </span>
                      )}
                    </div>
                  </div>
                  
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-ink-dim mt-3 text-center">
            Updates every 30 seconds
          </p>
        </div>
      )}

      <style>{`
        @keyframes pulse-animation {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.8;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .pulse-animation {
          animation: pulse-animation 0.6s ease-in-out;
        }
      `}</style>
    </div>
  );
};
